import {
  Bot, Workflow, MessageSquare,
  Search, BarChart3, ShieldCheck, Palette, Repeat,
  LayoutGrid, Infinity as InfinityIcon, Rocket, Briefcase, GraduationCap,
} from "lucide-react";
import { PRODUCTS } from "./products";

export const NAV_LINKS = [
  { label: "Home", href: "/" },
  { label: "Features", href: "/features" },
  { label: "Agency OS", href: "/agency-os", bold: true },
  { label: "Pricing", href: "/pricing" },
];

// External app - signup/login are handled on the product subdomain, not this marketing site.
export const SIGNUP_URL = "https://post.magicbolt.in/signup";
export const LOGIN_URL = "https://post.magicbolt.in/";

export const CHANNELS = [
  "Google Business Profile", "Facebook", "Instagram", "WhatsApp API CRM", "WordPress",
  "SEO", "AEO", "GEO",
];

export const FEATURES = [
  {
    icon: Bot,
    title: "AI Content Engine",
    desc: "Generate on-brand posts, blogs and replies for every client in seconds - trained on their voice, not generic templates.",
    span: "lg:col-span-2",
  },
  {
    icon: Workflow,
    title: "Multi-Channel Autopilot",
    desc: "One dashboard to publish and sync across GBP, Facebook, Instagram and WordPress.",
    span: "",
  },
  {
    icon: MessageSquare,
    title: "Smart Review & Inbox Replies",
    desc: "AI drafts human-quality responses to reviews and DMs, keeping every client hyper-responsive.",
    span: "",
  },
  {
    icon: Search,
    title: "SEO · AEO · GEO Optimizer",
    desc: "Rank in classic search, AI answer engines and generative results with automated on-page and entity optimization.",
    span: "lg:col-span-2",
  },
  {
    icon: BarChart3,
    title: "Auto-Generated Reports",
    desc: "Beautiful branded reports delivered on schedule - zero manual assembly.",
    span: "",
  },
  {
    icon: Repeat,
    title: "Workflow Automations",
    desc: "Chain triggers and actions so routine delivery runs itself, 24/7.",
    span: "",
  },
];

export const WHITELABEL_POINTS = [
  { icon: LayoutGrid, title: "Agency OS, One Login", desc: "GBP automation, social, WhatsApp API CRM, websites, QR review capture, digital menus and more - bundled under your single branded dashboard." },
  { icon: Palette, title: "Your Brand, Everywhere", desc: "Custom logo, domain, colors and login. Clients never see MagicBolt - only you." },
  { icon: ShieldCheck, title: "Resell With Confidence", desc: "Set your own prices, bill clients directly and keep 100% of the margin." },
  { icon: InfinityIcon, title: "Infinitely Scalable", desc: "From 5 clients to 5,000 on the same 100% cloud-hosted platform - zero servers for you to manage." },
];

export const PRODUCT_ARCHITECTURE = [
  {
    title: "AI AUTOMATION",
    count: "6 Product Categories",
    items: PRODUCTS.filter((p) => p.category === "AI Automation").map((p) => p.name),
  },
  {
    title: "QR-BASED SOFTWARE",
    count: "5 Products",
    items: PRODUCTS.filter((p) => p.subcategory === "QR").map((p) => p.name),
  },
];

const AI_AUTOMATION_NAMES = PRODUCT_ARCHITECTURE[0].items;
const QR_SOFTWARE_NAMES = PRODUCT_ARCHITECTURE[1].items;

export const PLANS = [
  {
    key: "qr-business",
    slug: "digital-software",
    name: "DIGITAL SOFTWARE",
    badge: null,
    tagline: "Launch your own QR-powered digital software business.",
    valueTagline: "Sell digital QR solutions under your own brand.",
    positioning:
      "Best for entrepreneurs and businesses that want to launch their own branded QR-based digital software platform.",
    setupFee: 50000,
    renewalAmount: 2000,
    renewalPeriod: "month",
    // Pricing views shown as tabs on the Pricing page (Unlimited level):
    // Monthly (setup + monthly fee), Annual (`yearly`) and Lifetime (`onetime`).
    pricing: {
      monthly: { setup: 5000, amount: 5000 },
      yearly: { amount: 50000 },
      onetime: { amount: 100000 },
    },
    differentiator: null,
    featureBlocks: [
      { title: "QR-BASED SOFTWARE", capacity: "Unlimited", items: QR_SOFTWARE_NAMES },
    ],
    businessFeatures: [
      "Unlimited Businesses",
      "Unlimited QR Software Usage",
      "White-Label Platform",
      "Custom Branding",
      "Custom Domain",
      "AI-Enabled Features",
    ],
    notIncluded: ["AI Automation Channels"],
    cta: "Launch Digital Software",
    microcopy: "Best starting point for a QR software business",
  },
  {
    key: "ai-agency",
    slug: "ai-agency",
    name: "AI AGENCY",
    badge: "MOST POPULAR",
    tagline: "Build and scale your own AI automation agency.",
    valueTagline: "Sell AI automation and business software to your clients.",
    positioning:
      "For marketing agencies, digital agencies, consultants and entrepreneurs who want to sell AI automation services under their own brand.",
    setupFee: 150000,
    renewalAmount: 10000,
    renewalPeriod: "month",
    pricing: {
      monthly: { setup: 12000, amount: 10000 },
      yearly: { amount: 100000 },
      onetime: { amount: 250000 },
    },
    differentiator: null,
    featureBlocks: [
      {
        title: "AI AUTOMATION",
        capacity: "Unlimited",
        items: AI_AUTOMATION_NAMES.filter(
          (name) =>
            name !== "Facebook Automation" && name !== "Instagram Automation" && name !== "WhatsApp API CRM" && name !== "AI Calling"
        ),
      },
      {
        title: "AI CAPABILITIES",
        capacity: null,
        items: [
          "AI Content Generation",
          "AI Review Replies",
          "AI Social Automation",
          "AI Comments Reply",
          "AEO/GEO Optimization",
          "Business Automation",
        ],
      },
      { title: "QR-BASED SOFTWARE", capacity: "Unlimited", items: QR_SOFTWARE_NAMES },
    ],
    businessFeatures: [
      "Unlimited Businesses",
      "Unlimited Clients",
      "White-Label Platform",
      "Custom Branding",
      "Custom Domain",
      "AI Enabled",
      "Agency/Client Management",
    ],
    notIncluded: null,
    cta: "Build Your AI Agency",
    microcopy: "Everything you need to sell AI automation + digital business solutions.",
  },
  {
    key: "ai-enterprise",
    slug: "ai-saas-business",
    name: "AI SAAS BUSINESS",
    badge: null,
    tagline: "The complete Magic Bolt ecosystem. Everything unlimited.",
    valueTagline: "Run the complete Magic Bolt ecosystem with unlimited access.",
    positioning:
      "For businesses and agencies that want the complete Magic Bolt platform without meaningful product or channel limits.",
    setupFee: 200000,
    renewalAmount: 15000,
    renewalPeriod: "month",
    pricing: {
      monthly: { setup: 15000, amount: 15000 },
      yearly: { amount: 120000 },
      onetime: { amount: 300000 },
    },
    differentiator: "Everything Unlimited",
    featureBlocks: [
      {
        title: "AI AUTOMATION",
        capacity: "Unlimited Channels",
        items: [
          ...AI_AUTOMATION_NAMES,
          "AI Content",
          "AI Review Replies",
          "AI Comments Reply",
          "Social Automation",
          "Future AI Automation Products",
        ],
      },
      { title: "QR-BASED SOFTWARE", capacity: "Unlimited", items: QR_SOFTWARE_NAMES },
    ],
    businessFeatures: [
      "Unlimited Businesses",
      "Unlimited Clients",
      "Unlimited Software Usage",
      "Unlimited AI Automation Channels",
      "White-Label Platform",
      "Custom Domain",
      "Custom Branding",
      "AI Enabled",
      "Agency/Reseller Capabilities",
      "Complete Magic Bolt Ecosystem",
    ],
    notIncluded: null,
    cta: "Go Unlimited",
    microcopy: "Own the complete AI-powered business platform.",
  },
];

// When prices below were last changed - surfaced in /products.json,
// /llms.txt and the pricing FAQ. Bump it whenever any price changes.
export const PRICING_LAST_UPDATED = "2026-09-29";
export const PRICING_CURRENCY = "INR";
// Every displayed price is shown as a limited-time 50% discount off a
// struck-through list price of 2x (see PricingPlanCard).
export const PRICING_PROMOTION = { discountPercent: 50, listPriceMultiplier: 2, expiry: null };

// --- Channel Based level -------------------------------------------------
// The channel calculator on the Pricing page (PricingLocationSlider): price
// scales linearly off the base tier and is billed only on paid channels -
// the bundled free ones don't add to the price. Lifetime = one payment of
// `lifetimeMultiplier` x the annual price.
export const CHANNEL_PRICING = {
  baseChannels: 10,
  baseYearly: 10000, // INR for 10 channels, billed yearly
  lifetimeMultiplier: 2,
  whiteLabelMinChannels: 20,
  // Below this the plan automates Google Business Profile only.
  allChannelsMinChannels: 40,
  stops: [
    // `whatsappFree` = extra WhatsApp API channels bundled free at the tier.
    { paid: 10, free: 0, whatsappFree: 0, total: 10, label: "10" },
    { paid: 20, free: 0, whatsappFree: 0, total: 20, label: "20" },
    { paid: 40, free: 0, whatsappFree: 0, total: 40, label: "40" },
    { paid: 70, free: 0, whatsappFree: 0, total: 70, label: "70" },
  ],
};
export const channelPrice = (paidChannels, billing = "yearly") => {
  const yearly = CHANNEL_PRICING.baseYearly * (paidChannels / CHANNEL_PRICING.baseChannels);
  return billing === "lifetime" ? yearly * CHANNEL_PRICING.lifetimeMultiplier : yearly;
};

// Cheapest single payment any plan can be bought at, in INR - used as
// `lowPrice` for the AggregateOffer in the SoftwareApplication JSON-LD.
// Monthly is excluded because it is a setup fee plus a recurring fee, not
// one price.
export const PLAN_LOW_PRICE = Math.min(
  ...PLANS.flatMap((p) => [p.pricing.yearly.amount, p.pricing.onetime.amount]),
  channelPrice(CHANNEL_PRICING.stops[0].paid, "yearly")
);

// Plain-language pricing, generated from the data above so FAQ answers,
// /llms.txt and /products.json can never drift from the plan cards.
export const formatINR = (n) => `₹${Math.round(n).toLocaleString("en-IN")}`;
// "AI SAAS BUSINESS" -> "AI SaaS Business"
const WORD_CASE = { AI: "AI", SAAS: "SaaS" };
export const planDisplayName = (name) =>
  name
    .split(" ")
    .map((w) => WORD_CASE[w] || w.charAt(0) + w.slice(1).toLowerCase())
    .join(" ");
export const PRICING_SUMMARY = {
  structure:
    "MagicBolt AI has two pricing levels. Channel Based plans are priced by how many channels you connect and are paid annually or once for lifetime. Unlimited plans (Digital Software, AI Agency, AI SaaS Business) can be paid monthly (a one-time setup fee plus a monthly fee), annually, or once for lifetime access.",
  unlimited: () =>
    PLANS.map(
      (p) =>
        `${planDisplayName(p.name)}: ${formatINR(p.pricing.monthly.setup)} setup + ${formatINR(p.pricing.monthly.amount)}/month, ${formatINR(p.pricing.yearly.amount)}/year, or ${formatINR(p.pricing.onetime.amount)} one-time (lifetime)`
    ),
  channel: () =>
    CHANNEL_PRICING.stops.map(
      (s) =>
        `${s.label} channels: ${formatINR(channelPrice(s.paid, "yearly"))}/year or ${formatINR(channelPrice(s.paid, "lifetime"))} lifetime`
    ),
};

// Channel-based tiers shown in the comparison table (and JSON-LD) behind
// the "Channel Based | Unlimited" toggle, capped to a channel allowance.
// Prices are derived from CHANNEL_PRICING so they always match the
// calculator the visitor actually sees.
export const CHANNEL_PLANS = [
  {
    key: "bronze",
    slug: "bronze",
    name: "BRONZE",
    badge: null,
    tagline: "10 Google Business Profile channels, without white-label.",
    channelAllowance: "10 Channels",
    pricing: {
      yearly: { amount: channelPrice(10, "yearly") },
      onetime: { amount: channelPrice(10, "lifetime") },
    },
    cardFeatures: [
      "10 Channels",
      "Google Business Profile Management",
      "AI Content & Review Replies",
      "QR Software Suite",
    ],
    cta: "Choose Bronze",
    microcopy: "For a single business or a first few clients.",
  },
  {
    key: "silver",
    slug: "silver",
    name: "SILVER",
    badge: null,
    tagline: "20 channels of any type - GMB, WhatsApp, WordPress and more.",
    channelAllowance: "20 Channels",
    pricing: {
      yearly: { amount: channelPrice(20, "yearly") },
      onetime: { amount: channelPrice(20, "lifetime") },
    },
    cardFeatures: [
      "20 Channels - Any Mix",
      "GMB, WhatsApp, WordPress",
      "AI Content & Review Replies",
      "QR Software Suite",
      "White-Label Platform",
      "Custom Branding & Domain",
    ],
    cta: "Choose Silver",
    microcopy: "Best for agencies just getting started.",
  },
  {
    key: "gold",
    slug: "gold",
    name: "GOLD",
    badge: "MOST POPULAR",
    tagline: "40 channels of any type - GMB, Facebook, Instagram, WhatsApp, WordPress and more.",
    channelAllowance: "40 Channels",
    pricing: {
      yearly: { amount: channelPrice(40, "yearly") },
      onetime: { amount: channelPrice(40, "lifetime") },
    },
    cardFeatures: [
      "40 Channels - Any Mix",
      "GMB, Facebook, Instagram, WhatsApp, WordPress",
      "WhatsApp API CRM",
      "AI Calling Agent",
      "AEO + GEO Optimization",
      "AI Content, Reviews & Workflows",
      "White-Label Platform",
      "Custom Branding & Domain",
    ],
    cta: "Choose Gold",
    microcopy: "The sweet spot for growing agencies.",
  },
  {
    key: "platinum",
    slug: "platinum",
    name: "PLATINUM",
    badge: null,
    tagline: "70 channels of any type for high-volume agencies.",
    channelAllowance: "70 Channels",
    pricing: {
      yearly: { amount: channelPrice(70, "yearly") },
      onetime: { amount: channelPrice(70, "lifetime") },
    },
    cardFeatures: [
      "70 Channels - Any Mix",
      "Every Automation Channel Included",
      "WhatsApp API CRM",
      "AI Calling Agent",
      "AEO + GEO Optimization",
      "AI Content, Reviews & Workflows",
      "Priority Support",
      "White-Label Platform",
    ],
    cta: "Choose Platinum",
    microcopy: "For agencies scaling past 50 clients.",
  },
];

// Comparison matrix for the Channel Based level - same shape/sections as
// COMPARISON_SECTIONS, keyed to the CHANNEL_PLANS keys.
export const CHANNEL_COMPARISON_SECTIONS = [
  {
    title: "CHANNELS & LIMITS",
    rows: [
      { label: "Total Channels", values: { bronze: "10", silver: "20", gold: "40", platinum: "70" } },
      { label: "Google Business Profile", values: { bronze: "Within Limit", silver: "Within Limit", gold: "Within Limit", platinum: "Within Limit" } },
      // The 10-channel tier is Google Business Profile only; every other
      // automation channel opens up from 20 channels (Facebook and Instagram from 40).
      { label: "Facebook Automation", values: { bronze: false, silver: false, gold: "Within Limit", platinum: "Within Limit" } },
      { label: "Instagram Automation", values: { bronze: false, silver: false, gold: "Within Limit", platinum: "Within Limit" } },
      { label: "WhatsApp API CRM", href: "https://waycrm.in", values: { bronze: false, silver: false, gold: true, platinum: true } },
      { label: "AI Calling Agent", values: { bronze: false, silver: false, gold: true, platinum: true } },
      { label: "WordPress Automation", values: { bronze: false, silver: "Within Limit", gold: "Within Limit", platinum: "Within Limit" } },
      { label: "Websites", values: { bronze: false, silver: "Within Limit", gold: "Within Limit", platinum: "Within Limit" } },
      { label: "AEO + GEO", unlimited: true, values: { bronze: false, silver: true, gold: true, platinum: true } },
    ],
  },
  {
    title: "QR-BASED SOFTWARE",
    // Included without limits - the table shows an infinity icon, not a tick.
    // A single row can set the same flag.
    unlimited: true,
    rows: QR_SOFTWARE_NAMES.map((name) => ({
      label: name,
      values: { bronze: true, silver: true, gold: true, platinum: true },
    })),
  },
  {
    title: "AGENCY FEATURES",
    rows: [
      { label: "White-Label", values: { bronze: false, silver: true, gold: true, platinum: true } },
      { label: "Custom Domain", values: { bronze: false, silver: true, gold: true, platinum: true } },
      { label: "Custom Branding", values: { bronze: false, silver: true, gold: true, platinum: true } },
      { label: "Unlimited Businesses", values: { bronze: true, silver: true, gold: true, platinum: true } },
      // Agency-platform tools (features.js `agency`), not AI features.
      { label: "Client Management", values: { bronze: true, silver: true, gold: true, platinum: true } },
      { label: "Plans & Usage Limits", values: { bronze: true, silver: true, gold: true, platinum: true } },
      { label: "Billing & Revenue", values: { bronze: true, silver: true, gold: true, platinum: true } },
      { label: "Bulk Mailing", values: { bronze: true, silver: true, gold: true, platinum: true } },
      { label: "Bring Your Own Keys", values: { bronze: true, silver: true, gold: true, platinum: true } },
    ],
  },
  {
    title: "AI CAPABILITIES",
    rows: [
      { label: "AI Content Generation", values: { bronze: true, silver: true, gold: true, platinum: true } },
      { label: "AI Review Replies", values: { bronze: true, silver: true, gold: true, platinum: true } },
      { label: "AI Comments Reply", values: { bronze: true, silver: true, gold: true, platinum: true } },
      { label: "AI Image Generation", values: { bronze: true, silver: true, gold: true, platinum: true } },
      { label: "One Composer, Every Channel", values: { bronze: true, silver: true, gold: true, platinum: true } },
      { label: "Content Calendar", values: { bronze: true, silver: true, gold: true, platinum: true } },
      { label: "Bulk Media Scheduler", values: { bronze: true, silver: true, gold: true, platinum: true } },
      { label: "Suggested Posts & Media Library", values: { bronze: true, silver: true, gold: true, platinum: true } },
      { label: "AI Autopilot", values: { bronze: true, silver: true, gold: true, platinum: true } },
      { label: "AI Posts & Captions", values: { bronze: true, silver: true, gold: true, platinum: true } },
      { label: "AI Assistant", values: { bronze: true, silver: true, gold: true, platinum: true } },
      { label: "MCP Connector", values: { bronze: true, silver: true, gold: true, platinum: true } },
    ],
  },
];

// Bundled with every plan purchase - shown on the Pricing page below the
// plan cards so the setup fee reads as more than just software access.
export const ADDONS = [
  {
    icon: Rocket,
    title: "Platform & Setup",
    items: [
      "White-Label Branding Setup",
      "Custom Domain Setup",
      "Technical Setup Assistance",
      "Priority Support",
      "Dedicated Launch Manager",
      "Agency Website",
    ],
  },
  {
    icon: Briefcase,
    title: "Sales & Client Acquisition",
    items: [
      "Sales & Proposal Kit",
      "Ready-to-Sell Service Packages",
      "Sales & Outreach Scripts",
      "Social Media Marketing & Client Onboarding Kit",
    ],
  },
  {
    icon: GraduationCap,
    title: "Training, Growth & Lead Generation",
    items: [
      "Agency Launch Training",
      "Sales & Product Training",
      "Agency Strategy Session",
      "90-Day Growth Blueprint",
      "6 Digital Marketing Courses",
      "Google Ranking Guide",
      "Lead Generation Strategy & Training",
      "500+ Leads of Potential Clients in Your City",
      "Leads Suggestion",
    ],
  },
];

// Full feature-matrix for the comparison table below the pricing cards.
// `values` is keyed by PLANS[].key; string = informational cell, boolean =
// checkmark ( true = ✓, false = ✕ ).
export const COMPARISON_SECTIONS = [
  {
    title: "AI AUTOMATION",
    rows: [
      { label: "Google Business Profile", values: { "qr-business": false, "ai-agency": "Unlimited", "ai-enterprise": "Unlimited" } },
      { label: "WordPress Automation", values: { "qr-business": false, "ai-agency": "Unlimited", "ai-enterprise": "Unlimited" } },
      { label: "Instant Website", values: { "qr-business": false, "ai-agency": "Unlimited", "ai-enterprise": "Unlimited" } },
      { label: "AEO + GEO", values: { "qr-business": false, "ai-agency": "Unlimited", "ai-enterprise": "Unlimited" } },
      { label: "Facebook Automation", values: { "qr-business": false, "ai-agency": false, "ai-enterprise": "Unlimited" } },
      { label: "Instagram Automation", values: { "qr-business": false, "ai-agency": false, "ai-enterprise": "Unlimited" } },
      { label: "WhatsApp API CRM", values: { "qr-business": false, "ai-agency": false, "ai-enterprise": "Unlimited" } },
      { label: "AI Calling Agent", values: { "qr-business": false, "ai-agency": false, "ai-enterprise": "Unlimited" } },
    ],
  },
  {
    title: "QR-BASED SOFTWARE",
    // Included without limits - the table shows an infinity icon, not a tick.
    // A single row can set the same flag.
    unlimited: true,
    rows: QR_SOFTWARE_NAMES.map((name) => ({
      label: name,
      values: { "qr-business": true, "ai-agency": true, "ai-enterprise": true },
    })),
  },
  {
    title: "AGENCY FEATURES",
    rows: [
      { label: "White-Label", values: { "qr-business": true, "ai-agency": true, "ai-enterprise": true } },
      { label: "Custom Domain", values: { "qr-business": true, "ai-agency": true, "ai-enterprise": true } },
      { label: "Custom Branding", values: { "qr-business": true, "ai-agency": true, "ai-enterprise": true } },
      { label: "Unlimited Businesses", values: { "qr-business": true, "ai-agency": true, "ai-enterprise": true } },
      { label: "Unlimited Clients", values: { "qr-business": true, "ai-agency": true, "ai-enterprise": true } },
      // Agency-platform tools (features.js `agency`), not AI features.
      { label: "Client Management", values: { "qr-business": true, "ai-agency": true, "ai-enterprise": true } },
      { label: "Plans & Usage Limits", values: { "qr-business": true, "ai-agency": true, "ai-enterprise": true } },
      { label: "Billing & Revenue", values: { "qr-business": true, "ai-agency": true, "ai-enterprise": true } },
      { label: "Bulk Mailing", values: { "qr-business": true, "ai-agency": true, "ai-enterprise": true } },
      { label: "Bring Your Own Keys", values: { "qr-business": true, "ai-agency": true, "ai-enterprise": true } },
    ],
  },
  {
    title: "AI CAPABILITIES",
    rows: [
      { label: "AI Content Generation", values: { "qr-business": true, "ai-agency": true, "ai-enterprise": true } },
      { label: "AI Review Replies", values: { "qr-business": true, "ai-agency": true, "ai-enterprise": true } },
      { label: "AI Comments Reply", values: { "qr-business": true, "ai-agency": true, "ai-enterprise": true } },
      { label: "AI Image Generation", values: { "qr-business": true, "ai-agency": true, "ai-enterprise": true } },
      { label: "Social Automation", values: { "qr-business": true, "ai-agency": true, "ai-enterprise": true } },
      { label: "One Composer, Every Channel", values: { "qr-business": true, "ai-agency": true, "ai-enterprise": true } },
      { label: "Content Calendar", values: { "qr-business": true, "ai-agency": true, "ai-enterprise": true } },
      { label: "Scheduled First Comments", values: { "qr-business": true, "ai-agency": true, "ai-enterprise": true } },
      { label: "Bulk Media Scheduler", values: { "qr-business": true, "ai-agency": true, "ai-enterprise": true } },
      { label: "Suggested Posts & Media Library", values: { "qr-business": true, "ai-agency": true, "ai-enterprise": true } },
      { label: "Brand Voice Agent", values: { "qr-business": true, "ai-agency": true, "ai-enterprise": true } },
      { label: "AI Autopilot", values: { "qr-business": true, "ai-agency": true, "ai-enterprise": true } },
      { label: "AI Posts & Captions", values: { "qr-business": true, "ai-agency": true, "ai-enterprise": true } },
      { label: "AI Assistant", values: { "qr-business": true, "ai-agency": true, "ai-enterprise": true } },
      { label: "MCP Connector", values: { "qr-business": true, "ai-agency": true, "ai-enterprise": true } },
    ],
  },
];

// Every plan has its own detail page at /pricing/:slug (PricingPlanDetailPage).
// `level` says which pricing level - and so which comparison matrix - it
// belongs to.
export const ALL_PLANS = [
  ...CHANNEL_PLANS.map((p) => ({ ...p, level: "channel" })),
  ...PLANS.map((p) => ({ ...p, level: "unlimited" })),
];
export const PLAN_LEVEL_LABEL = { channel: "Channel Based", unlimited: "Unlimited" };
export const planPath = (plan) => `/pricing/${plan.slug}`;
export const getPlanBySlug = (slug) => ALL_PLANS.find((p) => p.slug === slug);
export const planSections = (plan) =>
  plan.level === "channel" ? CHANNEL_COMPARISON_SECTIONS : COMPARISON_SECTIONS;
// Short "what you get" list: the card bullets (Channel Based) or the
// business features (Unlimited).
export const planHighlights = (plan) => plan.cardFeatures || plan.businessFeatures || [];

export const BUSINESS_CHANNELS = [
  { label: "GMB", brand: "google" },
  { label: "Facebook", brand: "facebook" },
  { label: "Instagram", brand: "instagram" },
  { label: "WhatsApp", brand: "whatsapp" },
  { label: "WordPress", brand: "wordpress" },
  { label: "GEO", brand: "geo" },
];

export const FAQS = [
  {
    q: "Can I sell this as my own product?",
    a: "Yes - that's what Agency OS is built for. You get a fully branded dashboard, custom domain, client billing and seat management, bundling every automation and software product under your name. Your clients only ever see your brand, never MagicBolt.",
  },
  {
    q: "What's included in Agency OS vs. buying tools separately?",
    a: "Agency OS bundles GBP automation, social publishing, instant websites, QR review capture, digital cards, bio links and more into one login - instead of stitching together five different subscriptions and reselling none of them under your brand.",
  },
  {
    q: "Which channels can I connect?",
    a: "Google Business Profile, Facebook, Instagram, WhatsApp (WhatsApp API CRM) and WordPress out of the box, plus dedicated SEO, AEO (answer engine) and GEO (generative engine) optimization workflows.",
  },
  {
    q: "Do I need technical skills to set it up?",
    a: "No. Connecting channels takes a few clicks, and our automations come pre-built. If you can use a social scheduler, you can run your own Agency OS.",
  },
  {
    q: "Is there a free trial?",
    a: "Every paid plan starts with a no-risk free trial. No credit card required to explore the platform.",
  },
  {
    q: "How does billing work?",
    a: `${PRICING_SUMMARY.structure} Prices are in INR and currently shown at a limited-time ${PRICING_PROMOTION.discountPercent}% discount - see the pricing page for every plan.`,
  },
];
